import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { motion } from "motion/react";
import { parseCommand } from "@otrack/commands";
import { isLive, runningEntry, startTimer, taskLabel } from "@otrack/domain";
import { Icon } from "@otrack/ui";
import { executeCommand, projectLabel, projectRefs, targetLabel } from "./commands";
import { useApp, useDataset } from "./context";
import { modifierKey, rankPalette, type PaletteItem, type PaletteSection } from "./palette";
import { navigate } from "./router";
import { describeIntent } from "./suggestions";

/**
 * The Command Palette: one searchable list of pages, Projects, Clients, and actions, opened with
 * Ctrl+K (⌘K) from anywhere. Anything else typed goes to the Command Bar.
 */
export function CommandPalette({ onClose, theme, toggleTheme, undo }: { onClose: () => void; theme: "light" | "dark"; toggleTheme: () => void; undo: () => void }) {
  const app = useApp();
  const { store, services } = app;
  const dataset = useDataset();
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    inputRef.current?.focus({ preventScroll: true });
    return () => {
      // Hand focus back unless the chosen item moved it somewhere on purpose.
      const current = document.activeElement;
      const lost = !current || current === document.body || panelRef.current?.contains(current);
      if (lost && previous?.isConnected) previous.focus({ preventScroll: true });
    };
  }, []);

  const items = useMemo(() => {
    const running = runningEntry(dataset);
    const factory = store.getState().factory;
    const projects = projectRefs(dataset);
    const labelOf = (ref: { id: string; name: string }) => {
      const task = dataset.tasks[ref.id];
      return task ? taskLabel(dataset, task) : ref.name;
    };
    const list: PaletteItem[] = [];

    if (running) {
      list.push({
        id: "stop", group: "Timer", label: "Stop timer", meta: targetLabel(dataset, running.projectId, running.taskId), keywords: "pause end", icon: "stop",
        run: () => {
          const result = executeCommand("stop", store.getState().dataset, factory, Date.now());
          if (result.type === "operations") void app.act(result.operations, result.message);
        }
      });
    }
    projects.filter((project) => project.id !== running?.projectId).forEach((project, index) => {
      const label = projectLabel(dataset, project.id);
      list.push({
        id: `start:${project.id}`, group: "Timer", label: `${running ? "Switch to" : "Start"} ${project.name}`, meta: project.clientName ?? undefined,
        keywords: "start timer track switch", icon: "play", searchOnly: index >= 3,
        run: () => void app.act(startTimer(store.getState().dataset, { projectId: project.id }, factory, Date.now()), `${running ? "Switched to" : "Started"} ${label}`)
      });
    });
    // Open Tasks, found by their own name or their Project's.
    for (const project of projects) {
      for (const task of project.tasks ?? []) {
        if (task.id === running?.taskId) continue;
        list.push({
          id: `start-task:${task.id}`, group: "Timer", label: `${running ? "Switch to" : "Start"} ${labelOf(task)}`,
          meta: [project.name, project.clientName].filter(Boolean).join(" · "), keywords: `start timer track switch task ${project.name} ${project.clientName ?? ""}`,
          icon: "target", searchOnly: true,
          run: () => void app.act(startTimer(store.getState().dataset, { projectId: project.id, taskId: task.id }, factory, Date.now()), `${running ? "Switched to" : "Started"} ${targetLabel(dataset, project.id, task.id)}`)
        });
      }
    }
    list.push({ id: "log", group: "Timer", label: "Log time", meta: "in the command bar", keywords: "worked add entry", icon: "clock", run: () => app.sendToCommandBar("worked 1h on ", false) });

    list.push(
      { id: "new-event", group: "Actions", label: "Plan an event", meta: "in the command bar", keywords: "new event schedule calendar meeting plan", icon: "calendar-plus", run: () => app.sendToCommandBar("plan ", false) },
      { id: "new-project", group: "Actions", label: "New project", keywords: "create add", icon: "plus", run: () => app.openNewProject() },
      { id: "new-client", group: "Actions", label: "New client", keywords: "create add customer", icon: "user", run: () => app.openNewClient() },
      { id: "new-invoice", group: "Actions", label: "New invoice", keywords: "create bill factura", icon: "receipt", run: () => app.openNewInvoice() },
      { id: "import", group: "Actions", label: "Import from solidtime", keywords: "csv upload", icon: "download", run: () => app.openImport() },
      { id: "theme", group: "Actions", label: theme === "dark" ? "Switch to light theme" : "Switch to dark theme", keywords: "appearance mode theme", icon: theme === "dark" ? "sun" : "moon", run: toggleTheme }
    );
    if (store.getState().canUndo()) list.push({ id: "undo", group: "Actions", label: "Undo last change", icon: "undo", shortcut: `${modifierKey()} Z`, run: undo });
    const { sync } = services;
    if (sync.mode === "signed-in" && sync.syncNow) list.push({ id: "sync", group: "Actions", label: "Sync now", keywords: "refresh vault", icon: "refresh", run: sync.syncNow });
    if (sync.mode === "signed-out" && sync.signIn) list.push({ id: "sign-in", group: "Actions", label: "Sign in to sync", keywords: "login account", icon: "sign-in", run: sync.signIn });
    if (sync.mode === "signed-in" && sync.signOut) list.push({ id: "sign-out", group: "Actions", label: "Sign out", meta: sync.account ?? undefined, keywords: "logout account", icon: "sign-out", searchOnly: true, run: sync.signOut });

    list.push(
      { id: "go:today", group: "Go to", label: "Today", keywords: "home day", icon: "clock", run: () => navigate({ page: "today" }) },
      { id: "go:calendar", group: "Go to", label: "Calendar", keywords: "week month plan schedule events agenda calendario", icon: "calendar", run: () => navigate({ page: "calendar", view: null, date: null }) },
      { id: "go:projects", group: "Go to", label: "Projects", keywords: "clients", icon: "folder", run: () => navigate({ page: "projects" }) },
      { id: "go:invoices", group: "Go to", label: "Invoices", keywords: "bill billing unpaid outstanding facturas", icon: "receipt", run: () => navigate({ page: "invoices" }) },
      { id: "go:settings", group: "Go to", label: "Settings", keywords: "preferences rate goal backup sync", icon: "settings", run: () => navigate({ page: "settings" }) },
      { id: "go:trash", group: "Go to", label: "Trash", keywords: "deleted restore", icon: "trash", run: () => navigate({ page: "trash" }) }
    );

    // Recently used Projects first, archived ones only when searched for.
    const recency = new Map(projects.map((project, index) => [project.id, index]));
    const allProjects = Object.values(dataset.projects)
      .filter(isLive)
      .sort((a, b) => (recency.get(a.id) ?? projects.length) - (recency.get(b.id) ?? projects.length) || a.name.localeCompare(b.name));
    allProjects.forEach((project, index) => {
      const client = project.clientId ? dataset.clients[project.clientId] : undefined;
      const archived = project.archivedAt !== null;
      list.push({
        id: `project:${project.id}`, group: "Projects", label: project.name, meta: archived ? "Archived" : client?.name, keywords: client?.name,
        icon: "folder", searchOnly: archived || index >= 5, run: () => navigate({ page: "project", id: project.id })
      });
    });
    for (const client of Object.values(dataset.clients).filter(isLive).sort((a, b) => a.name.localeCompare(b.name))) {
      list.push({
        id: `client:${client.id}`, group: "Clients", label: client.name, meta: client.archivedAt === null ? undefined : "Archived",
        icon: "briefcase", searchOnly: true, run: () => navigate({ page: "client", id: client.id })
      });
    }
    return list;
  }, [dataset, store, services, app, theme, toggleTheme, undo]);

  const sections = useMemo<PaletteSection[]>(() => {
    const ranked = rankPalette(items, query);
    const text = query.trim();
    if (!text) return ranked;
    const intent = parseCommand(text, { projects: projectRefs(dataset) });
    const { hint, icon } = describeIntent(intent, dataset, services.assistant !== null);
    const command: PaletteSection = {
      group: "Command bar",
      items: [{ id: "command", group: "Command bar", label: hint, meta: `“${text}”`, icon, shortcut: "Enter", run: () => app.sendToCommandBar(text, true) }]
    };
    // A command the Fast Path understands is most likely what was meant; a question goes last.
    return intent.kind === "unknown" ? [...ranked, command] : [command, ...ranked];
  }, [items, query, dataset, services.assistant, app]);

  const flat = sections.flatMap((section) => section.items);
  const current = Math.min(active, flat.length - 1);

  useEffect(() => {
    document.getElementById(`${listId}-${current}`)?.scrollIntoView?.({ block: "nearest" });
  }, [current, listId]);

  const choose = (item: PaletteItem | undefined) => {
    if (!item) return;
    onClose();
    item.run();
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActive((current + step + flat.length) % flat.length);
    } else if (event.key === "Home" || event.key === "End") {
      event.preventDefault();
      setActive(event.key === "Home" ? 0 : flat.length - 1);
    } else if (event.key === "Enter") {
      event.preventDefault();
      choose(flat[current]);
    } else if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      onClose();
    } else if (event.key === "Tab") {
      event.preventDefault();
    }
  };

  let index = -1;
  return <motion.div
    className="ot-palette-backdrop"
    role="presentation"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0, transition: { duration: 0.16 } }}
    transition={{ duration: 0.18 }}
    onMouseDown={onClose}
  >
    <motion.div
      ref={panelRef}
      className="ot-palette"
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      data-modal-escape-boundary="true"
      initial={{ opacity: 0, y: -10, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -6, scale: 0.985, transition: { duration: 0.14, ease: [0.4, 0, 1, 1] } }}
      transition={{ type: "spring", stiffness: 520, damping: 38 }}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <div className="ot-palette__search">
        <Icon name="search"/>
        <input
          ref={inputRef}
          value={query}
          onChange={(event) => { setQuery(event.target.value); setActive(0); }}
          onKeyDown={onKeyDown}
          role="combobox"
          aria-expanded="true"
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={flat.length ? `${listId}-${current}` : undefined}
          aria-label="Search commands"
          placeholder="Search projects, pages, and actions, or type a command"
          autoComplete="off"
          spellCheck={false}
        />
        <kbd aria-hidden="true">Esc</kbd>
      </div>
      <div className="ot-palette__list" id={listId} role="listbox" aria-label="Results">
        {sections.map((section) => <div key={section.group} role="group" aria-labelledby={`${listId}-${section.group}`} className="ot-palette__section">
          <div className="ot-palette__group" id={`${listId}-${section.group}`}>{section.group}</div>
          {section.items.map((item) => {
            index += 1;
            const position = index;
            return <div
              key={item.id}
              id={`${listId}-${position}`}
              role="option"
              aria-selected={position === current}
              className={`ot-palette__item ${position === current ? "is-active" : ""} ${item.group === "Command bar" ? "ot-palette__item--command" : ""}`}
              onMouseMove={() => { if (position !== current) setActive(position); }}
              onClick={() => choose(item)}
            >
              <span className="ot-palette__icon"><Icon name={item.icon}/></span>
              <span className="ot-palette__label">{item.label}</span>
              {item.meta && <span className="ot-palette__meta">{item.meta}</span>}
              {item.shortcut && <kbd className="ot-palette__shortcut" aria-hidden="true">{item.shortcut}</kbd>}
            </div>;
          })}
        </div>)}
      </div>
      <div className="ot-palette__foot" aria-hidden="true">
        <span><kbd>↑</kbd><kbd>↓</kbd>move</span>
        <span><kbd>Enter</kbd>open</span>
        <span className="ot-palette__foot-end"><kbd>{modifierKey()}</kbd><kbd>K</kbd>toggle</span>
      </div>
    </motion.div>
  </motion.div>;
}
